import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

// Rotas que não precisam de verificação de restaurante
const SKIP_RESTAURANT_CHECK_PATHS = [
  '/internal-support-alc2024',
  '/auth',
  '/pending-approval',
  '/access-denied',
  '/approval-success',
  '/forgot-password',
  '/terms',
  '/privacy',
];

// Zod schema for restaurant data validation - security defense-in-depth
const RestaurantDataSchema = z.object({
  restaurantName: z.string()
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(200, 'Nome muito longo')
    .regex(/^[a-zA-ZÀ-ÿ0-9\s&\-.']+$/, 'Nome contém caracteres inválidos'),
  phone: z.string()
    .regex(/^[\d\s\-+()]*$/, 'Formato de telefone inválido')
    .max(20, 'Telefone muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  whatsapp: z.string()
    .regex(/^[\d\s\-+()]*$/, 'Formato de WhatsApp inválido')
    .max(20, 'WhatsApp muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  address: z.string()
    .max(500, 'Endereço muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  city: z.string()
    .max(100, 'Cidade muito longa')
    .optional()
    .nullable()
    .transform(val => val || null),
  state: z.string()
    .max(2, 'Estado deve ter 2 letras')
    .optional()
    .nullable()
    .transform(val => val || null),
  zipCode: z.string()
    .regex(/^(\d{5}-?\d{3})?$/, 'CEP inválido')
    .optional()
    .nullable()
    .transform(val => val || null),
  instagram: z.string()
    .regex(/^(@?[a-zA-Z0-9._]*)?$/, 'Instagram inválido')
    .max(100, 'Instagram muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  website: z.string()
    .max(200, 'Website muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  facebook: z.string()
    .max(200, 'Facebook muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
});

interface BranchData {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface ManualReferralInfo {
  type: string;
  name: string;
  manual: boolean;
}

interface RestaurantData {
  restaurantName: string;
  cnpj?: string;
  totalUnits?: string; // CRM: total de unidades declaradas
  businessNiche?: string;
  phone?: string;
  whatsapp?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  instagram?: string;
  website?: string;
  facebook?: string;
  branches?: BranchData[];
  manualReferralInfo?: ManualReferralInfo;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string, restaurantData: RestaurantData, referralCode?: string, partnerSlug?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Verificar se está em rota que não precisa de restaurante
          const currentPath = window.location.pathname;
          const shouldSkipCheck = SKIP_RESTAURANT_CHECK_PATHS.some(
            path => currentPath.startsWith(path)
          );
          
          if (!shouldSkipCheck) {
            // User logged in, defer restaurant check
            setTimeout(() => {
              checkUserRestaurant(session.user.id);
            }, 0);
          }
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkUserRestaurant = async (userId: string) => {
    try {
      // Ignorar verificação para rotas de suporte e outras rotas especiais
      const currentPath = window.location.pathname;
      if (SKIP_RESTAURANT_CHECK_PATHS.some(path => currentPath.startsWith(path))) {
        return;
      }

      // Verificar se o usuário é de suporte
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      const isSupportUser = roles?.some(r => r.role === 'support') ?? false;
      if (isSupportUser) {
        return; // Não redirecionar usuários de suporte
      }

      // Verificar se é membro de restaurante (não apenas dono)
      const { data: membership } = await supabase
        .from('restaurant_members')
        .select('restaurant_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (membership) {
        return; // Já é membro de um restaurante
      }

      const { data: restaurant } = await (supabase as any)
        .from('restaurants')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (!restaurant) {
        // Redirect to onboarding if no restaurant
        navigate('/dashboard?onboarding=true');
      }
    } catch (error) {
      console.error('Error checking restaurant:', error);
    }
  };

  const signUp = async (email: string, password: string, name: string, restaurantData: RestaurantData, referralCode?: string, partnerSlug?: string) => {
    try {
      // Validate restaurant data with Zod schema - defense in depth
      const validatedData = RestaurantDataSchema.safeParse(restaurantData);
      
      if (!validatedData.success) {
        const firstError = validatedData.error.errors[0];
        toast.error(`Dados inválidos: ${firstError.message}`);
        throw new Error(firstError.message);
      }
      
      const safeRestaurantData = validatedData.data;
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name,
            restaurant_name: restaurantData.restaurantName,
            referred_by: referralCode || null,
            referred_by_partner: partnerSlug || null
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        // Use Edge Function with service_role to bypass RLS (user has no active session after signup)
        const { data: fnResult, error: fnError } = await supabase.functions.invoke('create-restaurant-signup', {
          body: {
            user_id: data.user.id,
            name: safeRestaurantData.restaurantName,
            niche: restaurantData.businessNiche || 'Restaurante',
            cnpj: restaurantData.cnpj || null,
            declared_total_units: parseInt(restaurantData.totalUnits || '1', 10),
            phone: safeRestaurantData.phone,
            whatsapp_number: safeRestaurantData.whatsapp,
            address: safeRestaurantData.address,
            city: safeRestaurantData.city,
            state: safeRestaurantData.state,
            zip_code: safeRestaurantData.zipCode,
            instagram: safeRestaurantData.instagram,
            website: safeRestaurantData.website,
            facebook: safeRestaurantData.facebook,
            contact_email: email,
            contact_name: name.substring(0, 200),
            contact_phone: safeRestaurantData.whatsapp || safeRestaurantData.phone,
            referred_by_code: referralCode ? referralCode.substring(0, 50) : null,
            referred_by_partner_slug: partnerSlug ? partnerSlug.substring(0, 50) : null,
            other_social_media: restaurantData.manualReferralInfo 
              ? { referral_source: restaurantData.manualReferralInfo }
              : null,
            branches: restaurantData.branches || [],
          },
        });

        if (fnError || !fnResult?.success) {
          console.error('Error creating restaurant via edge function:', fnError, fnResult);
          throw new Error(fnResult?.error || fnError?.message || 'Erro ao criar restaurante');
        }

        toast.success('Cadastro realizado! Aguarde a aprovação da nossa equipe.');
        navigate('/pending-approval');
      }
    } catch (error: any) {
      if (error.message?.includes('User already registered')) {
        toast.error('Este email já está cadastrado. Faça login.');
      } else {
        toast.error(error.message || 'Erro ao criar conta');
      }
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Increment login count for retention offer tracking
      if (data.user) {
        try {
          await (supabase as any).rpc('increment_login_count', { p_user_id: data.user.id });
        } catch (rpcError) {
          // Non-blocking - don't fail login if RPC fails
          console.warn('Failed to increment login count:', rpcError);
        }
      }

      // Invalidate subscription cache to force fresh data
      await queryClient.invalidateQueries({ queryKey: ['subscription'] });
      // Also invalidate retention offer data
      await queryClient.invalidateQueries({ queryKey: ['retention-offer'] });

      toast.success('Login realizado com sucesso!');
      // CRITICAL: DO NOT navigate here!
      // Navigation is handled by AuthRedirectResolver in Auth.tsx
      // This prevents race conditions and ensures proper role/subscription checks
      // before determining the correct destination
    } catch (error: any) {
      if (error.message.includes('Invalid login credentials')) {
        toast.error('Email ou senha incorretos');
      } else {
        toast.error(error.message || 'Erro ao fazer login');
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear all cached queries on logout
      queryClient.clear();
      
      // Clear localStorage app data on logout (preserve only version tracking)
      const versionKey = 'alacart-last-app-version';
      const version = localStorage.getItem(versionKey);
      localStorage.clear();
      if (version) localStorage.setItem(versionKey, version);
      sessionStorage.clear();
      
      setUser(null);
      setSession(null);
      toast.success('Logout realizado com sucesso!');
      navigate('/auth');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao fazer logout');
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};